import { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface AuthContextType {
  session: Session | null;
  userRole: string;
  userEmail: string | null;
  isLoading: boolean;
  refreshSession: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  userRole: "",
  userEmail: null,
  isLoading: true,
  refreshSession: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState("");
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  
  const initializeRef = useRef(false);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const roleCache = useRef<{ [userId: string]: string }>({});
  const isTabVisible = useRef(true);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const clearAuthState = useCallback(() => {
    setSession(null);
    setUserEmail(null);
    setUserRole("");
  }, []);

  const fetchUserRole = useCallback(async (userId: string) => {
    // Check cache first
    if (roleCache.current[userId]) {
      setUserRole(roleCache.current[userId]);
      return;
    }

    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      if (profile) {
        roleCache.current[userId] = profile.role;
        setUserRole(profile.role);
      }
    } catch (error) {
      console.error("Error fetching user role:", error);
      setUserRole("");
    }
  }, []);

  const refreshSession = useCallback(async () => {
    // Skip if already initialized
    if (initializeRef.current) {
      return;
    }

    try {
      const { data: { session: currentSession }, error } = await supabase.auth.getSession();
      
      if (error) throw error;

      if (currentSession) {
        setSession(currentSession);
        setUserEmail(currentSession.user.email);
        // Defer role fetching to avoid blocking
        setTimeout(() => {
          if (currentSession.user.id) {
            fetchUserRole(currentSession.user.id);
          }
        }, 0);
      } else {
        clearAuthState();
      }
    } catch (error) {
      console.error("Error in session refresh:", error);
      clearAuthState();
    }
  }, [fetchUserRole, clearAuthState]);

  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      clearAuthState();
    } catch (error) {
      console.error("Error signing out:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to sign out",
      });
    }
  }, [clearAuthState, toast]);

  useEffect(() => {
    if (initializeRef.current) return;
    initializeRef.current = true;

    // Set up visibility change listener to prevent unnecessary operations
    const handleVisibilityChange = () => {
      isTabVisible.current = !document.hidden;
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Safety timeout to ensure loading never stays true indefinitely
    loadingTimeoutRef.current = setTimeout(() => {
      console.log("Safety timeout triggered - forcing isLoading to false");
      setIsLoading(false);
    }, 4000);

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      // Skip processing if tab is not visible (except for initial session)
      if (!isTabVisible.current && event !== 'INITIAL_SESSION') {
        return;
      }
      
      console.log("Auth state changed:", event, !!newSession);
      
      // Clear safety timeout since we got a response
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      
      // Clear any pending debounced operations
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      
      if (event === 'SIGNED_OUT' || (event === 'INITIAL_SESSION' && !newSession)) {
        clearAuthState();
        setIsLoading(false);
      } else if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && newSession) {
        setSession(newSession);
        setUserEmail(newSession.user.email);
        setIsLoading(false);
        
        // Debounced role fetching to prevent rapid successive calls
        debounceTimeout.current = setTimeout(() => {
          if (newSession.user.id && isTabVisible.current) {
            fetchUserRole(newSession.user.id);
          }
        }, 100);
      }
    });

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const contextValue = useMemo(() => ({
    session,
    userRole,
    userEmail,
    isLoading,
    refreshSession,
    signOut
  }), [session, userRole, userEmail, isLoading, refreshSession, signOut]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};